/**
 * Built-in Window/Level presets (CT, Hounsfield Units).
 *
 * Lives in the visualization layer next to WindowLevelService so the service can
 * resolve preset ids without importing from the components layer.
 *
 * Preset values adapted from:
 * Stepwards — "CT Windowing (W/L) Presets"
 * https://www.stepwards.com/?page_id=21646
 * Accessed: 2026-06-15
 */
export const WINDOW_LEVEL_PRESETS = [
    { id: 'angio', label: 'Angio (CTA)', window: 800, level: 200 },
    { id: 'soft-tissue', label: 'Soft Tissue', window: 350, level: 50 },
    { id: 'lung', label: 'Lung', window: 1600, level: -600 },
    { id: 'bone', label: 'Bone', window: 2000, level: 500 },
    { id: 'brain', label: 'Brain', window: 70, level: 30 },
    { id: 'liver', label: 'Liver', window: 160, level: 60 },
] as const;

/**
 * Stable preset identifier union — derived from {@link WINDOW_LEVEL_PRESETS} so the
 * set of valid ids stays in sync with the presets themselves. Referenced by workflow
 * defaults and the active-preset check.
 */
export type WindowLevelPresetId = (typeof WINDOW_LEVEL_PRESETS)[number]['id'];

export interface WindowLevelPreset {
    /** Stable identifier — referenced by workflow defaults and the active-preset check. */
    id: WindowLevelPresetId;
    /** Human-readable label shown in the toolbar menu. */
    label: string;
    /** Window width in Hounsfield Units. */
    window: number;
    /** Window level (center) in Hounsfield Units. */
    level: number;
}

/** Resolve a preset by id, or null if unknown. */
export function getWindowLevelPreset(id: WindowLevelPresetId): WindowLevelPreset | null {
    return WINDOW_LEVEL_PRESETS.find((p) => p.id === id) ?? null;
}
